import { createSlice } from '@reduxjs/toolkit';
import { FACE_MATCH_ATTEMPT_STATUS } from 'utils/faceMatchEnforcement';

export const LOW_CONFIDENCE_THRESHOLD = 60;
export const SUCCESS_THRESHOLD = 80;

export const getConfidenceLevel = (similarity) => {
    if (similarity === null || similarity === undefined) return { level: 'none', label: 'No match yet', color: 'orange' };
    if (similarity >= 90) return { level: 'high', label: `High ${similarity.toFixed(0)}%`, color: 'green' };
    if (similarity >= LOW_CONFIDENCE_THRESHOLD) return { level: 'medium', label: `Medium ${similarity.toFixed(0)}%`, color: 'orange' };
    return { level: 'low', label: `Low ${similarity.toFixed(0)}%`, color: 'red' };
};

// The two failure toasts. Shared with the face match activity events, whose error_message must be what the agent saw.
export const FACE_MATCH_NO_FACE_MESSAGE = 'No face detected in one or both images';
export const getFaceMatchFailureMessage = (error) => `Face match failed: ${error?.message || 'Unknown error'}`;

const initialState = {
    isLoading: false,
    error: null,
    response: null,
    similarity: null,
    isMatch: false,
    sourceImage: null,
    targetImage: null,
    sourceCameraType: null,
    targetCameraType: null,
    transactionId: null,
    history: {},
    // The submit block reads this, not `history`: history keeps successes only, and an error has to block too.
    latestAttemptByTransactionId: {},
    selectedImages: {},
    currentMatchType: null,
};

const faceMatchSlice = createSlice({
    name: 'faceMatch',
    initialState,
    reducers: {
        compareFaces(state, { payload }) {
            state.isLoading = true;
            state.error = null;
            state.response = null;
            state.similarity = null;
            state.isMatch = false;
            state.sourceImage = payload.sourceImage;
            state.targetImage = payload.targetImage;
            state.sourceCameraType = payload.sourceCameraType;
            state.targetCameraType = payload.targetCameraType;
            state.transactionId = payload.transactionId;
            state.currentMatchType = payload.matchType || null;
            if (payload.transactionId) {
                // Keeps the last finished result, which compareFacesCancelled puts back if this attempt never ends.
                const current = state.latestAttemptByTransactionId[payload.transactionId];
                const previous = current?.status === FACE_MATCH_ATTEMPT_STATUS.PENDING ? current.previous : current;
                state.latestAttemptByTransactionId[payload.transactionId] = {
                    status: FACE_MATCH_ATTEMPT_STATUS.PENDING,
                    ...(previous && { previous }),
                };
            }
        },
        compareFacesSuccess(state, { payload }) {
            const { response, similarity, isMatch, transactionId, matchType, sourceImage, targetImage } = payload;

            state.isLoading = false;
            state.response = response;
            state.similarity = similarity;
            state.isMatch = isMatch;
            state.error = null;

            if (transactionId) {
                state.latestAttemptByTransactionId[transactionId] = { status: FACE_MATCH_ATTEMPT_STATUS.SUCCESS, similarity };
            }

            if (transactionId && matchType) {
                if (!state.history[transactionId]) {
                    state.history[transactionId] = { truck: [], car: [] };
                }

                const historyEntry = {
                    id: Date.now(),
                    timestamp: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
                    similarity,
                    confidence: getConfidenceLevel(similarity),
                    sourceImage,
                    targetImage,
                    response,
                };

                const type = matchType.toLowerCase();
                if (state.history[transactionId][type]) {
                    state.history[transactionId][type].unshift(historyEntry);
                }
            }
        },
        compareFacesFailure(state, { payload }) {
            state.isLoading = false;
            state.error = payload.error;
            state.response = null;
            state.similarity = null;
            state.isMatch = false;
            if (payload.transactionId) {
                state.latestAttemptByTransactionId[payload.transactionId] = {
                    status: FACE_MATCH_ATTEMPT_STATUS.ERROR,
                    errorCode: payload.error?.code ?? null,
                };
            }
        },
        // takeLatest cancelled this transaction's request, so its PENDING would never resolve and would lock its
        // submit dialog on "still running". The attempt never happened: the result before it comes back, or none.
        // A retry on the SAME transaction is skipped: its PENDING is the new attempt.
        compareFacesCancelled(state, { payload }) {
            const transactionId = payload?.transactionId;
            if (!transactionId || transactionId === state.transactionId) return;
            const attempt = state.latestAttemptByTransactionId[transactionId];
            if (attempt?.status !== FACE_MATCH_ATTEMPT_STATUS.PENDING) return;
            if (attempt.previous) state.latestAttemptByTransactionId[transactionId] = attempt.previous;
            else delete state.latestAttemptByTransactionId[transactionId];
        },
        resetFaceMatch(state) {
            state.isLoading = false;
            state.error = null;
            state.response = null;
            state.similarity = null;
            state.isMatch = false;
            state.sourceImage = null;
            state.targetImage = null;
            state.sourceCameraType = null;
            state.targetCameraType = null;
            state.transactionId = null;
            state.currentMatchType = null;
        },
        setLoading(state, { payload }) {
            state.isLoading = payload;
        },
        clearHistory(state, { payload }) {
            const transactionId = payload?.transactionId;
            if (transactionId) {
                delete state.history[transactionId];
                delete state.selectedImages[transactionId];
                delete state.latestAttemptByTransactionId[transactionId];
            } else {
                state.history = {};
                state.selectedImages = {};
                state.latestAttemptByTransactionId = {};
            }
        },
        setSelectedImage(state, { payload }) {
            const { transactionId, matchType, imageType, imageData } = payload;

            if (!state.selectedImages[transactionId]) {
                state.selectedImages[transactionId] = {
                    truck: { face: null, id: null },
                    car: { face: null, id: null },
                };
            }

            state.selectedImages[transactionId][matchType][imageType] = imageData;
        },
        clearSelectedImages(state, { payload }) {
            const { transactionId, matchType } = payload;
            if (state.selectedImages[transactionId]) {
                if (matchType) {
                    state.selectedImages[transactionId][matchType] = { face: null, id: null };
                } else {
                    state.selectedImages[transactionId] = {
                        truck: { face: null, id: null },
                        car: { face: null, id: null },
                    };
                }
            }
        },
    },
});

export const faceMatchActions = faceMatchSlice.actions;
export const faceMatchReducer = faceMatchSlice.reducer;
